// src/parametres/SocieteTaxes.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Spinner, Form, InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function SocieteTaxes() {
    const navigate = useNavigate();
    const [taxes, setTaxes] = useState({
        tva_taux: 0.00,
        css_taux: 0.00,
        tva_active: true,
        css_active: true
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchTaxes();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchTaxes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setTaxes(result.data);
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       HANDLERS
       ============================================================ */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTaxes({
            ...taxes,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleReset = async () => {
        await fetchTaxes();
        showToast('info', 'Réinitialisé', 'Les modifications ont été annulées');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'update_taxes',
                        ...taxes
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Enregistré', 'Taxes mises à jour avec succès');
            } else {
                showToast('error', 'Erreur', "Erreur lors de la sauvegarde");
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       LOADING — Bootstrap spinner (conservé)
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <Spinner
                                animation="border"
                                role="status"
                                className="mb-3"
                                style={{ width: '3rem', height: '3rem', color: COLORS.primary }}
                            >
                                <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                            <h5 className="text-muted">Chargement des taxes...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-percent-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Taxes (TVA / CSS)</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-settings-3-line"></i>
                                        Configuration des taux de taxes
                                    </span>
                                    {taxes.tva_active && (
                                        <span className="badge-modern" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-check-line"></i> TVA {taxes.tva_taux}%
                                        </span>
                                    )}
                                    {taxes.css_active && (
                                        <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-check-line"></i> CSS {taxes.css_taux}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/parametres')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== FORMULAIRE ===== */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="card-header-gradient">
                            <h5 className="mb-0 text-white d-flex align-items-center">
                                <i className="ri-percent-line me-2"></i>
                                Configuration des taxes
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4">

                                    {/* ===== TVA ===== */}
                                    <Col md={6}>
                                        <div className="tax-section-charte">
                                            <div className="section-title-charte">
                                                <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                    <i className="ri-percent-line"></i>
                                                </span>
                                                <h6>TV</h6>
                                            </div>
                                            <p className="text-muted small mb-3">Taxe sur la Valeur Ajoutée</p>

                                            <Form.Group className="mb-4">
                                                <Form.Check
                                                    type="switch"
                                                    id="tva-active"
                                                    name="tva_active"
                                                    label="Activer la TVA"
                                                    checked={taxes.tva_active}
                                                    onChange={handleChange}
                                                    className="switch-charte fw-semibold"
                                                />
                                            </Form.Group>

                                            {taxes.tva_active && (
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Taux de TVA (%)</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-percent-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            name="tva_taux"
                                                            value={taxes.tva_taux}
                                                            onChange={handleChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                        <InputGroup.Text className="bg-light">%</InputGroup.Text>
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Taux par défaut : 0%
                                                    </Form.Text>
                                                </Form.Group>
                                            )}
                                        </div>
                                    </Col>

                                    {/* ===== CSS ===== */}
                                    <Col md={6}>
                                        <div className="tax-section-charte">
                                            <div className="section-title-charte">
                                                <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                                    <i className="ri-shield-check-line"></i>
                                                </span>
                                                <h6>CSS</h6>
                                            </div>
                                            <p className="text-muted small mb-3">Contribution Sociale de Solidarité</p>

                                            <Form.Group className="mb-4">
                                                <Form.Check
                                                    type="switch"
                                                    id="css-active"
                                                    name="css_active"
                                                    label="Activer la CSS"
                                                    checked={taxes.css_active}
                                                    onChange={handleChange}
                                                    className="switch-charte fw-semibold"
                                                />
                                            </Form.Group>

                                            {taxes.css_active && (
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Taux de CSS (%)</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-percent-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            name="css_taux"
                                                            value={taxes.css_taux}
                                                            onChange={handleChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                        <InputGroup.Text className="bg-light">%</InputGroup.Text>
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Taux par défaut : 0%
                                                    </Form.Text>
                                                </Form.Group>
                                            )}
                                        </div>
                                    </Col>

                                    {/* ===== BOUTONS ===== */}
                                    <Col md={12}>
                                        <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                        <div className="d-flex justify-content-end gap-3 flex-wrap">
                                            <Button
                                                type="button"
                                                className="btn-modern-outline"
                                                onClick={handleReset}
                                            >
                                                <i className="ri-refresh-line me-1"></i> Réinitialiser
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="btn-modern-primary"
                                                disabled={saving}
                                            >
                                                {saving ? (
                                                    <><Spinner size="sm" className="me-2" />Sauvegarde...</>
                                                ) : (
                                                    <><i className="ri-save-line me-2"></i>Enregistrer</>
                                                )}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* ===== INFO ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mt-4">
                        <Card.Body className="p-4">
                            <div className="info-banner-charte">
                                <div className="info-banner-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                    <i className="ri-information-line"></i>
                                </div>
                                <div>
                                    <h6 className="fw-semibold mb-1">Information</h6>
                                    <p className="small text-muted mb-0">
                                        Les taux de TVA et CSS configurés ici seront appliqués par défaut lors de la création
                                        de nouveaux produits, devis, factures et bons de livraison. Vous pouvez modifier ces taux
                                        individuellement pour chaque document.
                                    </p>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== TAX SECTION ===== */
                .tax-section-charte {
                    background: #f8fafc;
                    padding: 1.5rem;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .tax-section-charte:hover {
                    border-color: var(--secondary);
                    box-shadow: 0 8px 24px rgba(33, 172, 205, 0.08);
                    background: white;
                }

                /* ===== FORMULAIRES ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: white; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }

                /* ===== SWITCH ===== */
                .switch-charte .form-check-input {
                    width: 2.5em;
                    height: 1.25em;
                    cursor: pointer;
                    background-color: #cbd5e1;
                    border-color: #cbd5e1;
                    transition: all 0.2s ease;
                }
                .switch-charte .form-check-input:checked {
                    background-color: var(--secondary);
                    border-color: var(--secondary);
                }
                .switch-charte .form-check-input:focus {
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.15);
                    border-color: var(--secondary);
                }
                .switch-charte .form-check-label {
                    cursor: pointer;
                    font-weight: 600;
                    color: #334155;
                }

                /* ===== INFO BANNER ===== */
                .info-banner-charte {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .info-banner-icon-charte {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.3rem;
                    flex-shrink: 0;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
